import React from "react";
import { useState, useRef } from "react";
import {
  Row,
  Col,
  Image,
  Container,
  ListGroup,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Meta from "../components/Meta";
import {
  useSubmitCareerMutation,
  useUploadCareerSubmissionFileMutation,
} from "../slices/submissionApiSlice";
import Loader from "../components/Loader";
import { validateForm, formatPhoneNumber } from "../utils/formValidation";
import RecaptchaCheckbox from "../components/RecaptchaCheckbox";

const CareerScreen = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [jobCategory, setJobCategory] = useState(""); // Job Category
  const [coverLetter, setCoverLetter] = useState(null); // Cover Letter
  const [resume, setResume] = useState(null); // Resume File
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const recaptchaRef = useRef(null);

  const [submitCareer, { isLoading: isSubmitting }] = useSubmitCareerMutation();
  const [uploadCareerSubmissionFile, { isLoading: isUploading }] =
    useUploadCareerSubmissionFileMutation();

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    // Skip validation for email field - let browser handle it
    if (field !== "email") {
      validateField(field);
    }
  };

  const validateField = (field) => {
    const formData = { name, email, phone, jobCategory };
    const validation = validateForm(formData, "career");
    setErrors((prev) => ({
      ...prev,
      [field]: validation.errors[field] || null,
    }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    if (touched.phone) {
      validateField("phone");
    }
  };

  const handlePhoneBlur = () => {
    setTouched({ ...touched, phone: true });
    // Format phone number on blur
    const formatted = formatPhoneNumber(phone);
    setPhone(formatted);
    validateField("phone");
  };

  const uploadFileHandler = async (e, fileType) => {
    const fileData = new FormData();
    fileData.append("file", e.target.files[0]);
    fileData.append("formType", "career");
    fileData.append("fileType", fileType); // either 'coverLetter' or 'resume'
    try {
      const uploadResponse = await uploadCareerSubmissionFile(
        fileData
      ).unwrap();
      toast.success(`${fileType} uploaded successfully!`);
      if (fileType === "coverLetter") {
        setCoverLetter(uploadResponse.file);
      } else {
        setResume(uploadResponse.file);
      }
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const formData = {
      name,
      email,
      phone: phone.replace(/\D/g, ""), // Store only digits
      jobCategory,
      coverLetter,
      resume,
    };

    // Validate entire form
    const validation = validateForm(formData, "career");

    if (!validation.isValid) {
      setErrors(validation.errors);
      setTouched({
        name: true,
        email: true,
        phone: true,
        jobCategory: true,
      });
      toast.error("Please correct the errors in the form");
      return;
    }

    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA checkbox");
      return;
    }

    try {
      await submitCareer({ ...formData, recaptchaToken }).unwrap();
      recaptchaRef.current?.reset();
      setRecaptchaToken("");
      toast.success("Submitting form...");
      navigate("/career");
      toast.success("Application submitted successfully!");

      // Clear the form
      setName("");
      setEmail("");
      setPhone("");
      setJobCategory("");
      setCoverLetter(null);
      setResume(null);
      setErrors({});
      setTouched({});
    } catch (err) {
      recaptchaRef.current?.reset();
      setRecaptchaToken("");
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <>
      <Meta
        title="Careers | Lugo Parts & Restoration"
        description="Join our team of passionate experts specializing in classic GMC truck restorations. Explore career opportunities that allow you to turn your passion for vintage GMC trucks into a rewarding profession."
        keywords="Careers, Team, Passionate, Classic GMC Trucks, Restoration, Experts, Opportunities, Vintage, Rewarding, Profession, Job Openings, Apply, Skills, Craftsmanship, Quality, Professional, Automotive, Culture, Benefits"
      />

      <Container>
        <Alert
          variant="info"
          className="mt-4 mb-4"
          style={{ borderLeft: "4px solid #d80607" }}
        >
          <Alert.Heading style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
            Interested in joining our team?
          </Alert.Heading>
          <p className="mb-0" style={{ fontSize: "0.95rem" }}>
            If you'd like to submit an application, you can find our application
            form at the bottom of this page.
          </p>
        </Alert>
        {/* Join Our Passion-Driven Team */}
        <section className="about-us mt-5 mb-5">
          <h3 className="mb-5">Join Our Passion-Driven Team</h3>
          <Row className="align-items-center">
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    Are you ready to embark on a transformative journey in the
                    world of classic vehicle restoration? Become an integral
                    part of our passion-driven team at Lugo Parts and
                    Restoration.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
            <Col md={6}>
              <Image
                src="/images/compressed/webp/Careers01.webp"
                fluid
                rounded
              />
            </Col>
          </Row>
        </section>

        {/* Why Lugo Parts & Restoration */}
        <section className="why-lugo mt-5 mb-5">
          <h3 className="mb-5">Why Lugo Parts & Restoration?</h3>
          <Row className="align-items-center">
            <Col md={6}>
              <Image
                src="/images/compressed/webp/Restoration06.webp"
                fluid
                rounded
              />
            </Col>
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <p className="landing-page-text">
                    At Lugo Parts & Restoration, we believe that the key to a
                    successful restoration project lies in the hands of the
                    people who bring it to life. Our team is a diverse group of
                    skilled artisans, engineers, and enthusiasts who share a
                    common passion for classic vehicles.
                    <br />
                    <br />
                    We offer a collaborative work environment that encourages
                    innovation and rewards dedication. If you're looking to turn
                    your passion into a fulfilling career, you've found your
                    home.
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
        </section>

        {/* Career Opportunities */}
        <section className="career-opportunities mt-5 mb-5">
          <h3 className="mb-5">Explore Our Career Opportunities</h3>
          <p className="landing-page-text">
            Use the form below to apply for a position at Lugo Parts &
            Restoration. Complete all required fields, attach your resume, and
            feel free to include a cover letter to introduce yourself. We look
            forward to potentially having you join our talented team!
          </p>
          <br />
          <p className="landing-page-text">
            <strong>Note:</strong> Applicants must be 18 years of age or older
            to apply.
          </p>
          <br />
          <p className="landing-page-text">
            If you experience any issues uploading your resume or cover letter,
            please contact us by calling{" "}
            <a href="tel:916-652-0840">916-652-0840</a> or email us at{" "}
            <a href="mailto:lugoparts@yahoo.com">lugoparts@yahoo.com</a>
          </p>
        </section>

        {/* Submit Your Application */}
        <section className="submit-application mt-5 mb-5">
          <h3 className="mb-5" style={{ textAlign: "center" }}>
            Submit Your Application
          </h3>
          <Form onSubmit={submitHandler}>
            <Form.Group controlId="name" className="my-3">
              <Form.Label>Your Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => handleBlur("name")}
                className={errors.name && touched.name ? "is-invalid" : ""}
                required
              />
              {errors.name && touched.name && (
                <div className="form-error">{errors.name}</div>
              )}
            </Form.Group>

            <Form.Group controlId="email" className="my-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur("email")}
                required
              />
            </Form.Group>

            <Form.Group controlId="phone" className="my-3">
              <Form.Label>Phone Number *</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter your phone number (with area code)"
                value={phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                className={errors.phone && touched.phone ? "is-invalid" : ""}
                required
              />
              {errors.phone && touched.phone && (
                <div className="form-error">{errors.phone}</div>
              )}
              <Form.Text className="text-muted">
                Include area code (e.g., 555-123-4567)
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="jobCategory" className="my-3">
              <Form.Label>Job Category *</Form.Label>
              <Form.Select
                value={jobCategory}
                onChange={(e) => setJobCategory(e.target.value)}
                onBlur={() => handleBlur("jobCategory")}
                className={
                  errors.jobCategory && touched.jobCategory ? "is-invalid" : ""
                }
                required
              >
                <option value="">Select Job Category</option>
                <option value="Automotive Painter">Automotive Painter</option>
                <option value="Automotive Mechanic">Automotive Mechanic</option>
                <option value="Body Technician">Body Technician</option>
                <option value="Metal Fabricator">Metal Fabricator</option>
                <option value="Shipping & Receiving">
                  Shipping & Receiving
                </option>
                <option value="Receptionist">Receptionist</option>
                <option value="Shop Helper">Shop Helper</option>
              </Form.Select>
              {errors.jobCategory && touched.jobCategory && (
                <div className="form-error">{errors.jobCategory}</div>
              )}
            </Form.Group>

            <Form.Group controlId="coverLetter" className="my-3">
              <Form.Label>Cover Letter (PDF only) *</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                onChange={(e) => uploadFileHandler(e, "coverLetter")}
                required
              />
            </Form.Group>

            <Form.Group controlId="resume" className="my-3">
              <Form.Label>Upload Resume (PDF only) *</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                onChange={(e) => uploadFileHandler(e, "resume")}
                required
              />
            </Form.Group>

            <p className="small text-muted mb-3">
              By submitting this application, you confirm that you are at least
              18 years of age and agree to our{" "}
              <a href="/privacy" className="text-decoration-none">
                Privacy Policy
              </a>
              .
            </p>
            <p className="small text-muted mb-3">
              If you experience any issues uploading your resume or cover
              letter, please contact us by calling{" "}
              <a href="tel:916-652-0840">916-652-0840</a> or email us at{" "}
              <a href="mailto:lugoparts@yahoo.com">lugoparts@yahoo.com</a>
            </p>

            <RecaptchaCheckbox
              ref={recaptchaRef}
              action="career_submit"
              onTokenChange={setRecaptchaToken}
            />

            <Button
              type="submit"
              variant="primary"
              className="mt-2"
              disabled={isSubmitting || isUploading}
            >
              Submit Application
            </Button>
            {(isSubmitting || isUploading) && <Loader />}
          </Form>
        </section>
      </Container>
    </>
  );
};

export default CareerScreen;
